// Floating live-chat widget with AI assistant (Talkani Assistant)
// Bottom-right corner. Polls every 4s for new messages. Persists across reloads.
import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, User as UserIcon, ArrowRight, Bell } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { requestNotificationPermission, showNotification } from "../lib/notifications";
import {
  LS_CONV, LS_TOKEN, LS_NAME, LS_EMAIL,
  readSeenKeys, markKeySeen,
  clearChatConversation, persistChatIdentity,
} from "../lib/chatStorage";
import useContextualTeaser from "../hooks/useContextualTeaser";
import useConsent from "../hooks/useConsent";

const POLL_MS = 4000;
const TEASER_DELAY_MS = 3500;

export default function ChatWidget() {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const { consent } = useConsent();
  const ct = t.chat;
  const location = useLocation();

  // Context-aware teaser: returns { key, text } so we can both render the
  // service-specific copy AND track which keys this visitor has already seen.
  const teaser = useContextualTeaser(location, ct);

  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(() => localStorage.getItem(LS_CONV) || "");
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [name, setName] = useState(() => localStorage.getItem(LS_NAME) || "");
  const [email, setEmail] = useState(() => localStorage.getItem(LS_EMAIL) || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [closed, setClosed] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  // First-visit attention teaser ("1" badge + speech bubble) — shown once per
  // browser, after a short delay, dismissed when the user opens the chat or
  // explicitly closes the teaser. Intercom-style pattern.
  const [showTeaser, setShowTeaser] = useState(false);

  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const lastSeenIdRef = useRef(null);

  // Auto-fill name/email if logged in
  useEffect(() => {
    if (user) {
      if (!name) setName(user.name || "");
      if (!email) setEmail(user.email || "");
    }
  }, [user, name, email]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Mark messages as seen when widget is open
  useEffect(() => {
    if (open && messages.length > 0) {
      lastSeenIdRef.current = messages[messages.length - 1].message_id;
      setHasNew(false);
    }
  }, [open, messages]);

  // Per-key contextual teaser — fires once per teaser key per browser. When the
  // route changes to a different teaser key the user hasn't seen yet, we re-arm
  // the timer so the new contextual message can appear. Suppressed entirely on
  // legal pages (teaser.key === null) and during an active conversation.
  useEffect(() => {
    if (typeof window === "undefined") return;
    setShowTeaser(false); // reset on every route/key change
    if (!teaser.key || !teaser.text) return;
    if (conversationId) return; // returning conversation — no teaser
    if (open) return; // chat already open
    if (readSeenKeys().includes(teaser.key)) return; // already shown this one
    const tm = setTimeout(() => {
      setShowTeaser(true);
      // Fire-and-forget analytics: record that this contextual teaser was shown.
      // Only if the user has consented to analytics (DSGVO).
      if (consent.analytics) {
        api.post("/teasers/event", {
          teaser_key: teaser.key,
          action: "shown",
          lang,
          path: `${location.pathname}${location.search}${location.hash}`,
        }).catch(() => { /* silent */ });
      }
    }, TEASER_DELAY_MS);
    return () => clearTimeout(tm);
  }, [teaser.key, teaser.text, conversationId, open, lang, location.pathname, location.search, location.hash, consent.analytics]);

  // Dismiss teaser as soon as the chat opens and remember this key as seen.
  // Also record a "converted" event so we can measure which teasers drive chats.
  useEffect(() => {
    if (open && showTeaser) {
      setShowTeaser(false);
      if (teaser.key) {
        markKeySeen(teaser.key);
        if (consent.analytics) {
          api.post("/teasers/event", {
            teaser_key: teaser.key,
            action: "converted",
            lang,
            path: `${location.pathname}${location.search}${location.hash}`,
          }).catch(() => { /* silent */ });
        }
      }
    }
  }, [open, showTeaser, teaser.key, lang, location.pathname, location.search, location.hash, consent.analytics]);

  const fetchMessages = useCallback(async () => {
    if (!conversationId || !accessToken) return;
    try {
      const r = await api.get(`/chat/${conversationId}/messages`, {
        params: { access_token: accessToken },
      });
      const newMsgs = r.data.messages || [];
      setMessages(newMsgs);
      setClosed(r.data.status === "closed");
      // Detect unseen messages while widget is closed
      if (!open && newMsgs.length > 0) {
        const lastId = newMsgs[newMsgs.length - 1].message_id;
        if (lastSeenIdRef.current && lastId !== lastSeenIdRef.current) {
          const lastMsg = newMsgs[newMsgs.length - 1];
          if (lastMsg.sender !== "user") setHasNew(true);
        } else if (!lastSeenIdRef.current) {
          lastSeenIdRef.current = lastId;
        }
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        // conversation invalid — reset
        resetConversation();
      }
    }
  }, [conversationId, accessToken, open]);

  // Polling
  useEffect(() => {
    if (!conversationId || !accessToken) return;
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [conversationId, accessToken, fetchMessages]);

  const resetConversation = () => {
    clearChatConversation();
    setConversationId("");
    setAccessToken("");
    setMessages([]);
    setClosed(false);
    lastSeenIdRef.current = null;
  };

  const startConversation = async (e) => {
    e?.preventDefault();
    setError("");
    if (!name.trim() || !email.trim() || !input.trim()) {
      setError(ct.error_required);
      return;
    }
    setStarting(true);
    try {
      const r = await api.post("/chat/start", {
        name: name.trim(),
        email: email.trim(),
        lang,
        first_message: input.trim(),
      });
      const { conversation_id, access_token } = r.data;
      persistChatIdentity(conversation_id, access_token, name.trim(), email.trim());
      setConversationId(conversation_id);
      setAccessToken(access_token);
      setInput("");
    } catch (err) {
      setError(err?.response?.data?.detail || ct.error_generic);
    } finally {
      setStarting(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const text = input.trim();
    setSending(true);
    setError("");
    // Optimistic
    const optimistic = {
      message_id: `tmp_${Date.now()}`,
      conversation_id: conversationId,
      sender: "user",
      sender_name: name,
      text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setInput("");
    try {
      await api.post(`/chat/${conversationId}/message`, {
        access_token: accessToken,
        text,
      });
      // fetch will replace optimistic with real
      setTimeout(fetchMessages, 400);
    } catch (err) {
      setError(err?.response?.data?.detail || ct.error_generic);
      // Remove optimistic on error
      setMessages((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
    } finally {
      setSending(false);
    }
  };

  const needsForm = !conversationId || !accessToken;
  const formatTime = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString(lang === "ar" ? "ar-DE" : lang, {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  // Parse "[BOOK_NOW:service]" tag emitted by AI to render an inline "Book now" CTA
  const parseBookingCta = (text) => {
    const m = (text || "").match(/\[BOOK_NOW:([a-z_-]+)\]/i);
    if (!m) return { cleanText: text, bookService: null };
    const cleanText = text.replace(m[0], "").trim();
    return { cleanText, bookService: m[1].toLowerCase() };
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? ct.close : ct.open}
        data-testid="chat-widget-toggle"
        className="fixed bottom-20 end-5 z-[60] group"
      >
        {!open && (
          <span className="absolute inset-0 rounded-full bg-[#FF5A00] opacity-30 animate-ping" />
        )}
        <span className="relative flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#FF5A00] text-white shadow-2xl shadow-[#FF5A00]/40 hover:bg-[#E04F00] transition-all duration-200 hover:scale-105">
          {open ? <X size={26} strokeWidth={2.5} /> : <MessageCircle size={26} strokeWidth={2.5} />}
        </span>
        {/* Unread badge (real new messages while widget closed) */}
        {hasNew && !open && (
          <span className="absolute -top-1 -end-1 flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold border-2 border-white">
            !
          </span>
        )}
        {/* First-visit teaser badge — "1" indicator on the bubble */}
        {!open && !hasNew && showTeaser && (
          <span
            className="absolute -top-1 -end-1 flex items-center justify-center w-5 h-5 rounded-full bg-[#FF5A00] text-white text-[10px] font-black border-2 border-white animate-[pulse_1.6s_ease-in-out_infinite]"
            data-testid="chat-widget-teaser-badge"
          >
            1
          </span>
        )}
      </button>

      {/* Contextual per-page teaser speech bubble */}
      {!open && showTeaser && teaser.text && (
        <div
          key={teaser.key}
          className="fixed bottom-[150px] end-5 z-[59] max-w-[260px] bg-white dark:bg-[#0F1012] border border-neutral-200 dark:border-neutral-800 shadow-2xl shadow-black/20 ps-4 pe-9 py-3 animate-[slideUp_.45s_cubic-bezier(.16,1,.3,1)]"
          data-testid="chat-widget-teaser-bubble"
          data-teaser-key={teaser.key}
          style={{ borderRadius: "14px 14px 14px 2px" }}
        >
          <div className="flex items-start gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[#FF5A00] flex items-center justify-center text-white font-black text-[11px] flex-shrink-0">
              T<span className="text-black">.</span>
            </div>
            <div>
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#FF5A00] mb-1">
                Talkani
              </div>
              <p className="text-[13px] text-neutral-700 dark:text-neutral-200 leading-snug">
                {teaser.text}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowTeaser(false);
              if (teaser.key) {
                markKeySeen(teaser.key);
                if (consent.analytics) {
                  api.post("/teasers/event", {
                    teaser_key: teaser.key,
                    action: "dismissed",
                    lang,
                    path: `${location.pathname}${location.search}${location.hash}`,
                  }).catch(() => { /* silent */ });
                }
              }
            }}
            aria-label={ct.close || "Schließen"}
            data-testid="chat-widget-teaser-close"
            className="absolute top-1.5 end-1.5 w-6 h-6 flex items-center justify-center text-neutral-400 hover:text-neutral-700 dark:hover:text-white transition rounded-full"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-24 end-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[400px] max-w-[400px] bg-white dark:bg-[#0F1012] border border-neutral-200 dark:border-neutral-800 shadow-2xl flex flex-col overflow-hidden"
          style={{ height: "min(640px, calc(100vh - 8rem))" }}
          data-testid="chat-widget-panel"
        >
          {/* Header */}
          <div className="bg-[#090A0B] text-white px-5 py-4 flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-[#FF5A00] flex items-center justify-center font-black">
                T<span className="text-black">.</span>
              </div>
              <span className="absolute -bottom-0.5 -end-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#090A0B]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black tracking-tight">{ct.header_title}</div>
              <div className="text-[11px] text-neutral-400 flex items-center gap-1">
                <Sparkles size={10} className="text-[#FF5A00]" />
                <span>{ct.header_subtitle}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={async () => { await requestNotificationPermission(); }}
              title={ct.enable_notifications || "Enable notifications"}
              aria-label={ct.enable_notifications || "Enable notifications"}
              className="text-neutral-400 hover:text-[#FF5A00] transition flex-shrink-0 p-1.5"
              data-testid="chat-widget-notify-toggle"
            >
              <Bell size={16} />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              title={ct.close || "Schließen"}
              aria-label={ct.close || "Schließen"}
              className="text-neutral-400 hover:text-white transition flex-shrink-0 p-1.5"
              data-testid="chat-widget-close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          {needsForm ? (
            <form
              onSubmit={startConversation}
              className="flex-1 flex flex-col gap-3 p-5 overflow-y-auto"
              data-testid="chat-widget-form"
            >
              <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-1 leading-relaxed">
                {ct.form_intro}
              </p>
              <label className="block">
                <span className="block text-[11px] font-bold tracking-widest uppercase text-neutral-500 mb-1">
                  {ct.field_name}
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={ct.field_name_placeholder}
                  className="w-full border border-neutral-300 dark:border-neutral-700 px-3 py-2 text-sm bg-white dark:bg-neutral-900 focus:outline-none focus:border-[#FF5A00]"
                  data-testid="chat-widget-name"
                  required
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-bold tracking-widest uppercase text-neutral-500 mb-1">
                  {ct.field_email}
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={ct.field_email_placeholder}
                  className="w-full border border-neutral-300 dark:border-neutral-700 px-3 py-2 text-sm bg-white dark:bg-neutral-900 focus:outline-none focus:border-[#FF5A00]"
                  data-testid="chat-widget-email"
                  required
                />
              </label>
              <label className="block flex-1 flex flex-col">
                <span className="block text-[11px] font-bold tracking-widest uppercase text-neutral-500 mb-1">
                  {ct.field_message}
                </span>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={ct.field_message_placeholder}
                  rows={4}
                  className="w-full border border-neutral-300 dark:border-neutral-700 px-3 py-2 text-sm bg-white dark:bg-neutral-900 focus:outline-none focus:border-[#FF5A00] resize-none"
                  data-testid="chat-widget-first-message"
                  required
                />
              </label>
              {error && (
                <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 px-3 py-2">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={starting}
                className="bg-[#FF5A00] text-white font-bold tracking-wide uppercase text-sm py-3 hover:bg-[#E04F00] transition disabled:opacity-60"
                data-testid="chat-widget-start-btn"
              >
                {starting ? ct.starting : ct.start_btn}
              </button>
              <p className="text-[10px] text-neutral-400 text-center leading-relaxed">
                {ct.privacy_note}
              </p>
            </form>
          ) : (
            <>
              {/* Messages */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-neutral-50 dark:bg-[#0A0B0D]"
                data-testid="chat-widget-messages"
              >
                {messages.length === 0 && (
                  <div className="text-center text-xs text-neutral-400 py-8">{ct.loading}</div>
                )}
                {messages.map((m) => {
                  const isUser = m.sender === "user";
                  const isAI = m.sender === "ai";
                  const { cleanText, bookService } = isAI || m.sender === "admin"
                    ? parseBookingCta(m.text)
                    : { cleanText: m.text, bookService: null };
                  return (
                    <div
                      key={m.message_id}
                      className={`flex items-end gap-2 ${isUser ? "justify-end" : "justify-start"}`}
                      data-testid={`chat-msg-${m.sender}`}
                    >
                      {!isUser && (
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isAI ? "bg-gradient-to-br from-[#FF5A00] to-[#FF8A3D]" : "bg-[#090A0B]"
                          } text-white text-[10px] font-black`}
                        >
                          {isAI ? <Sparkles size={12} /> : "T"}
                        </div>
                      )}
                      <div className={`max-w-[80%] ${isUser ? "items-end" : "items-start"} flex flex-col`}>
                        <div
                          className={`px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                            isUser
                              ? "bg-[#FF5A00] text-white rounded-2xl rounded-ee-sm"
                              : isAI
                              ? "bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 border border-neutral-200 dark:border-neutral-800 rounded-2xl rounded-es-sm"
                              : "bg-[#090A0B] text-white rounded-2xl rounded-es-sm"
                          }`}
                        >
                          {cleanText}
                        </div>
                        {bookService && (
                          <Link
                            to={`/book?service=${encodeURIComponent(bookService)}`}
                            onClick={() => setOpen(false)}
                            className="mt-1.5 inline-flex items-center gap-1.5 bg-[#FF5A00] hover:bg-[#E04F00] text-white text-xs font-bold tracking-wider uppercase px-3 py-2 transition"
                            data-testid={`chat-bookcta-${bookService}`}
                          >
                            {ct.book_cta || "Jetzt buchen"} <ArrowRight size={12} />
                          </Link>
                        )}
                        <div className="text-[10px] text-neutral-400 mt-1 px-1">
                          {isAI ? ct.ai_label : isUser ? ct.you_label : m.sender_name || ct.admin_label}
                          {" · "}
                          {formatTime(m.created_at)}
                        </div>
                      </div>
                      {isUser && (
                        <div className="w-7 h-7 rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center flex-shrink-0">
                          <UserIcon size={14} className="text-neutral-600 dark:text-neutral-300" />
                        </div>
                      )}
                    </div>
                  );
                })}
                {closed && (
                  <div className="text-center text-xs text-neutral-500 py-4 border-t border-neutral-200 dark:border-neutral-800 mt-4">
                    {ct.closed_msg}
                    <button
                      type="button"
                      onClick={resetConversation}
                      className="block mx-auto mt-2 text-[#FF5A00] font-bold underline"
                      data-testid="chat-widget-new-btn"
                    >
                      {ct.start_new}
                    </button>
                  </div>
                )}
              </div>

              {/* Input */}
              {!closed && (
                <form onSubmit={sendMessage} className="border-t border-neutral-200 dark:border-neutral-800 p-3 flex gap-2 bg-white dark:bg-[#0F1012]">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={ct.input_placeholder}
                    className="flex-1 border border-neutral-300 dark:border-neutral-700 px-3 py-2 text-sm bg-white dark:bg-neutral-900 focus:outline-none focus:border-[#FF5A00]"
                    disabled={sending}
                    data-testid="chat-widget-input"
                  />
                  <button
                    type="submit"
                    disabled={sending || !input.trim()}
                    className="bg-[#FF5A00] text-white px-4 py-2 hover:bg-[#E04F00] transition disabled:opacity-60 flex items-center justify-center"
                    aria-label={ct.send}
                    data-testid="chat-widget-send-btn"
                  >
                    <Send size={16} />
                  </button>
                </form>
              )}
              {error && (
                <div className="px-4 py-2 text-xs text-red-600 bg-red-50 dark:bg-red-900/20">{error}</div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
